import {AxiosInstance, AxiosPromise, AxiosRequestConfig} from 'axios'
import {
  ArticleListRequestParam,
  ContentListRequestParam, ContentStatus,
  ItemsResponse,
  ListResponse, LiveRequestParam, SearchRequestParam,
  TagListRequestParam,
  TagParam,
} from '/@src/models/api'
import {
  ContentStatusValue,
  FaqContent, LinkContent,
  RawArticle,
  RawRoadshow,
  RawVideo,
  RoadmapContent
} from "/@src/models/content";
import gql from 'graphql-tag'
import {FaqItem, Tag} from "/@src/models/graphApi";

export function getArticles(
  api: AxiosInstance,
  param: ArticleListRequestParam
): Promise<ListResponse<RawArticle>> {
  const data: AxiosRequestConfig = {
    params: {
      limit: param.limit,
      page: param.page,
      keyword: param.keywords,
      theme_id: param.tag,
      author_id: param.userId,
      // 暂时写死，新版本文章不分类型
      article_type: "post",
      status: param.status,
    }
  }
  return api.get('/v1/admin/article', data)
}

export function fetchArticle(
  api: AxiosInstance,
  id: number
): Promise<RawArticle> {
  return api.get(`/v1/admin/article/${id}`)
}

export function deleteArticle(
  api: AxiosInstance,
  id: number
): Promise<RawArticle> {
  return api.put(`/v1/admin/article/${id}/status`,{
    status:'deleted'
  })
}

export function changeArticleStatus(
  api: AxiosInstance,
  id: number,
  status:ContentStatus
): Promise<RawArticle> {
  return api.put(`/v1/admin/article/${id}/status`,{
    status:status
  })
}

export function postArticle(
  api: AxiosInstance,
  article: RawArticle
): Promise<RawArticle> {
  if(article.id){
    return api.put(`/v1/admin/article/${article.id}`,article)
  }else {
    return api.post('/v1/admin/article',article)
  }
}
export function fetchVideo(
  api: AxiosInstance,
  id: number
): Promise<RawVideo> {
  return api.get(`/v1/admin/video/${id}`)
}
export function postVideo(
  api: AxiosInstance,
  video: RawVideo
): Promise<RawVideo> {
  return api.post(`/v1/admin/video/?type=v2`,video)
}

export function deleteVideo(
  api: AxiosInstance,
  id: number
): AxiosPromise {
  return api.post(`/v1/admin/video/status?type=v2`,{
    id:id,
    status:ContentStatusValue.DELETED
  })
}

export function fetchVideos(
  api: AxiosInstance,
  param: SearchRequestParam
): Promise<ItemsResponse<RawVideo>> {
  const data: AxiosRequestConfig = {
    params: {
      size: param.limit,
      anchor: param.page,
      q: param.keywords,
      status: param.status,
    },
  }
  return api.get(`/v1/admin/video/list`,data)
}

export function postGraphql<T>(
  api: AxiosInstance,
  operationName: string,
  query:string,
  variables:any
): Promise<T> {
  return api.post('/graphql',{
    operationName:operationName,
    query:query,
    variables:variables
  })
}

/**
 * graphql接口，获取单个文章
 */
export const FindArticleDocument = gql`
    query findArticle($id: Int!) {
  article(id: $id) {
    id
    title
    summary
    body
    preview
    cover_url
    display_time
    author {
      id
      nickname
      avatar
      description
    }
    free_at
    free_expired_at
    is_cooperation
    planned_published_at
    source_url
    status
    tags {
      id
      name
      description
      cover_url
    }
    tag_relations {
      id
      group
    }
    vip_visibility
    words_count
    attachments {
      title
      file_url
    }
  }
}
    `
/**
 * graphql接口，获取视频列表
 */
export const FindVideoDocument = gql`
        query findVideo($id: Int!) {
  video(id: $id) {
    id
    author {
      id
      nickname
    }
    title
    cover_url
    status
    description
    display_time
    planned_published_at
    vip_visibility
    related_article_ids
    related_report_ids
    related_articles {
      id
      title
      author {
        id
        nickname
        avatar
      }
      display_time
    }
    related_reports {
      id
      title
      display_time
      extension
      url
    }
    counter {
      viewed
    }
    tags {
      id
      name
      cover_url
    }
    tag_relations {
      id
      group
    }
    episodes {
      id
      name
      duration
      url
      preview_url
      thumbnail_urls
      size
    }
  }
}
    `;
/**
 * graphql接口，获取文章列表
 */
export const FindArticleListDocument = gql`
    query findArticleList($track: String, $first: Int, $offset: Int, $status: Int, $user_id: Int, $tag_id: Int, $group: Int, $wc_gt: Int, $wc_lt:Int, $vip_visibility:Int) {
  articles(
    user_id: $user_id
    first: $first
    offset: $offset
    status: $status
    track: $track
    tag_id: $tag_id
    vip_visibility: $vip_visibility
    group: $group
    wc_gt: $wc_gt
    wc_lt: $wc_lt
  ) {
    items {
      id
      title
      summary
      cover_url
      display_time
      planned_published_at
      is_cooperation
      vip_visibility
      words_count
      status
      counter {
        viewed
        commented
      }
      tags {
        id
        name
        cover_url
        is_topic
      }
      tag_relations{
        id
        group
      }
      author {
        id
        nickname
        avatar
      }
    }
    total_count
  }
}
    `;
/**
 * 文章简易信息，用于列表等
 */
export const ArticleSimpleFieldFragmentDoc = gql`
    fragment ArticleSimpleField on Article {
  id
  title
  is_cooperation
  words_count
  status
  planned_published_at
  tag_relations {
    id
    group
  }
}
    `;

/**
 * 文章完整信息
 */
export const ArticleFieldFragmentDoc = gql`
    fragment ArticleField on Article {
  id
  title
  body
  is_cooperation
  words_count
  planned_published_at
  source_url
  status
  attachments{
    id
    title
    file_url
    file_extension
  }
  tag_relations {
    id
    group
  }
}
    `;
/**
 * graphql接口，获取content列表
 */
export const FindContentsDocument = gql`
    query findContents($first: Int, $offset: Int, $vip_visibility: Int, $group: Int, $tag_id: Int, $user_id: Int, $kinds: [Int], $track: String, $status: Int, $tags: [Int], $start_time: Timestamp, $end_time: Timestamp) {
  contents(
    first: $first
    offset: $offset
    vip_visibility: $vip_visibility
    tag_id: $tag_id
    group: $group
    user_id: $user_id
    kinds: $kinds
    track: $track
    status: $status
    tags: $tags
    start_time: $start_time
    end_time: $end_time
  ) {
    items {
      raw_id
      title
      cover_url
      description
      status
      counter {
        viewed
        commented
      }
      author {
        id
        nickname
        avatar
      }
      vip_visibility
      datetime
      kind
      tags {
        id
        name
        is_topic
      }
      detail {
        ... on Article {
          ...ArticleSimpleField
        }
        ... on Video {
          id
          title
          tags {
            id
          }
          planned_published_at
          tag_relations {
            id
            group
          }
          episodes {
            duration
          }
        }
        ... on Report {
          id
          title
        }
        ... on AudioEpisode {
          id
          name
          tag_relations {
            id
            group
          }
        }
        ... on Roadshow {
          id
          title
          tag_relations {
            id
            group
          }
        }
        ...on Link{
          id
          title
          to
          tags{
            id
            name
            cover_url
          }
          tag_relations{
            id
            group
          }
        }
      }
    }
    total_count
  }
}
${ArticleSimpleFieldFragmentDoc}
    `;

/**
 * graphql接口，获取content完整列表(数据量较大)
 */
export const FindContentsFullDocument = gql`
    query findContents($first: Int, $offset: Int,$vip_visibility: Int, $group: Int, $tag_id: Int, $user_id: Int, $kinds: [Int], $track: String, $status: Int, $tags: [Int], $start_time: Timestamp, $end_time: Timestamp,$after: String, $sortBy: String) {
  contents(
    first: $first
    offset: $offset
    vip_visibility: $vip_visibility
    tag_id: $tag_id
    group: $group
    user_id: $user_id
    kinds: $kinds
    track: $track
    status: $status
    tags: $tags
    start_time: $start_time
    end_time: $end_time
    after: $after
    sort_by: $sortBy
  ) {
    items {
      raw_id
      title
      cover_url
      description
      status
      counter {
        viewed
        commented
      }
      author {
        id
        nickname
        avatar
      }
      vip_visibility
      datetime
      kind
      tags {
        id
        name
        is_topic
      }
      detail {
        ... on Article {
         ...ArticleField
        }
        ... on Video {
          id
          title
          tags {
            id
          }
          planned_published_at
          tag_relations {
            id
            group
          }
          episodes {
            duration
          }
        }
        ... on Report {
          id
          title
        }
        ... on AudioEpisode {
          id
          name
          tag_relations {
            id
            group
          }
        }
        ... on Roadshow {
          id
          title
          tag_relations {
            id
            group
          }
        }
        ...on Link{
          id
          title
          to
          tags{
            id
            name
            cover_url
          }
          tag_relations{
            id
            group
          }
        }
      }
    }
    page_info{
      end_cursor
      has_next_page
    }
    total_count
  }
}
${ArticleFieldFragmentDoc}
    `;

/**
 * graphql接口，获取Faq列表
 */
export const FindFaqDocument = gql`
    query findFaq($group: Int, $tag_id: Int) {
  contents(tag_id: $tag_id, group: $group) {
    items {
      raw_id
      detail {
        ... on Faq {
          id
          items {
            title
            body
            link
          }
          tag_relations {
            id
            group
          }
        }
      }
    }
  }
}
    `;
/**
 * graphql接口，获取单个content
 */
export const FindContentDocument = gql`
    query findContent($id: Int, $kind: Int) {
  content(id: $id, kind: $kind) {
    raw_id
    title
    description
    datetime
    cover_url
    kind
    author {
      id
      nickname
      avatar
    }
    tags {
      id
      name
    }
    vip_visibility
    detail {
        ... on Article {
          id
          title
          tag_relations {
            id
            group
          }
        }
        ... on Video {
          id
          title
          tag_relations {
            id
            group
          }
        }
        ... on Report {
          id
          title
          tag_relations {
            id
            group
          }
        }
        ... on AudioEpisode {
          id
          name
          tag_relations {
            id
            group
          }
        }
        ... on Roadshow {
          id
          title
          tag_relations {
            id
            group
          }
        }
      }
  }
}
    `;


export function postFaq(
  api: AxiosInstance,
  faq: FaqContent
): Promise<FaqContent> {
  return api.post('/faq',faq)
}

export function createRoadmap(
  api: AxiosInstance,
  data: RoadmapContent
): Promise<RoadmapContent> {
  return api.post('/roadmap',data)
}

export function updateRoadmap(
  api: AxiosInstance,
  data: RoadmapContent
): Promise<RoadmapContent> {
  return api.put('/roadmap',data)
}

export const FindRoadmapDocument = gql`
    query findRoadmap($tag_id: Int, $group: Int, $kinds: [Int]) {
  contents(tag_id: $tag_id, group: $group, kinds: $kinds) {
    items {
      raw_id
      detail {
        ... on RoadMap {
          id
          content
          tag_relations {
            id
            group
          }
        }
      }
    }
  }
}
    `;

export function fetchRoadshowList(
  api: AxiosInstance,
  param: LiveRequestParam
): Promise<ListResponse<RawRoadshow>> {
  const data: AxiosRequestConfig = {
    params: {
      first: param.limit,
      offset: param.offset,
      live_status: param.status
    }
  }
  return api.get('/v1/admin/roadshow/list', data)
}

export function updateRoadshowStatus(
  api: AxiosInstance,
  id: number,
  status:ContentStatusValue
): AxiosPromise {
  return api.post('/v1/admin/roadshow/status', {
    id: id,
    status: status
  })
}

export function fetchRoadshow(
  api: AxiosInstance,
  id: number
): Promise<RawRoadshow> {
  return api.get(`/v1/admin/roadshow/${id}`)
}

export function postRoadshow(
  api: AxiosInstance,
  roadshow: RawRoadshow
): Promise<RawRoadshow> {
  return api.post(`/v1/admin/roadshow`,roadshow)
}


export const FindRoadshowDocument = gql`
    query findRoadshow($id: Int) {
  roadshow(id: $id) {
    id
    title
    summary
    people
    status
    live_status
    live_method
    poster
    start_time
    end_time
    created_at
    body
    vip_visibility
    tags {
      id
      name
      description
    }
    tag_relations {
      id
      group
    }
    video_id
    video {
      id
      title
      cover_url
    }
  }
}
    `;
export function newLink(
  api: AxiosInstance,
  link: LinkContent
): Promise<LinkContent> {
  return api.post('/link',link)
}

export function updateLinkContent(
  api: AxiosInstance,
  link: LinkContent
): Promise<LinkContent> {
  return api.put('/link',link)
}

export function getLinkContent(
  api: AxiosInstance,
  id: number
): Promise<LinkContent> {
  return api.get(`/link/${id}`)
}

export function deleteLinkContent(
  api: AxiosInstance,
  id: number
): AxiosPromise {
  return api.delete('/link',{
    data:{
      id:id
    }
  })
}
